import React, { useState } from 'react';
import {
  Plus, Edit, Trash2, Send, Users, Clock, BookOpen, Smartphone,
  CheckCircle, XCircle, RefreshCw, MessageSquare, AlertCircle,
} from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import AdminLayout from '../../components/admin/AdminLayout';
import CustomerFormModal from '../../components/admin/CustomerFormModal';
import WhatsAppQRConnect from '../../components/admin/WhatsAppQRConnect';
import SendOfferModal from '../../components/admin/SendOfferModal';
import { GET_CLIENTS, GET_AGENCY_BY_NAME } from '../../graphql/admin/queries/client';
import { CREATE_CLIENT, UPDATE_CLIENT, DELETE_CLIENT } from '../../graphql/admin/mutations/client';
import { GET_WHATSAPP_ACCOUNT, GET_WHATSAPP_MESSAGES, GET_WHATSAPP_TEMPLATES } from '../../graphql/queries/whatsapp';
import {
  DISCONNECT_WHATSAPP,
  CREATE_WHATSAPP_TEMPLATE,
  UPDATE_WHATSAPP_TEMPLATE,
  DELETE_WHATSAPP_TEMPLATE,
} from '../../graphql/mutations/whatsapp';
import { useAuth } from '../../lib/auth';
import LoadingSpinner from '../../components/LoadingSpinner';
import type { WhatsAppAccount, WhatsAppMessage, WhatsAppTemplate, WhatsAppTemplateInput } from '../../types/whatsapp';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Client {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: string;
  createdAt: string;
  agency: { id: number; name: string };
}

type Tab = 'clients' | 'overview' | 'history' | 'templates';

// ─── Statuts WhatsApp ──────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  PENDING:   { label: 'En attente',  className: 'bg-yellow-100 text-yellow-700' },
  SENT:      { label: 'Envoyé',      className: 'bg-blue-100 text-blue-700' },
  DELIVERED: { label: 'Délivré',     className: 'bg-indigo-100 text-indigo-700' },
  READ:      { label: 'Lu',          className: 'bg-green-100 text-green-700' },
  FAILED:    { label: 'Échoué',      className: 'bg-red-100 text-red-700' },
};

const emptyTemplate: WhatsAppTemplateInput = { name: '', content: '', language: 'fr', variables: [], isDefault: false };

// ─── Composant ────────────────────────────────────────────────────────────────

export default function ClientsPage() {
  const { user } = useAuth();
  const agencyName = user?.agencyName || '';

  // Tabs
  const [activeTab, setActiveTab] = useState<Tab>('clients');

  // Clients state
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [offerTargets, setOfferTargets] = useState<Client[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  // Templates state
  const [templateForm, setTemplateForm] = useState<WhatsAppTemplateInput>(emptyTemplate);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data: agencyData } = useQuery(GET_AGENCY_BY_NAME, {
    variables: { name: agencyName },
    skip: !agencyName,
  });
  const agencyId: number | undefined = agencyData?.agency?.id;

  const { data: clientsData, loading: clientsLoading, refetch: refetchClients } = useQuery(GET_CLIENTS, {
    variables: { agencyName },
    skip: !agencyName,
    fetchPolicy: 'network-only',
  });

  const { data: accountData, loading: accountLoading, refetch: refetchAccount } = useQuery(GET_WHATSAPP_ACCOUNT, {
    fetchPolicy: 'network-only',
    skip: !user,
  });

  const { data: messagesData, loading: messagesLoading, refetch: refetchMessages } = useQuery(GET_WHATSAPP_MESSAGES, {
    variables: { limit: 50, offset: 0 },
    fetchPolicy: 'network-only',
    skip: !user || activeTab !== 'history',
  });

  const { data: templatesData, refetch: refetchTemplates } = useQuery(GET_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'network-only',
    skip: !user || activeTab !== 'templates',
  });

  // ── Mutations ─────────────────────────────────────────────────────────────────

  const [createClient] = useMutation(CREATE_CLIENT, {
    onCompleted: () => { refetchClients(); setClientModalOpen(false); setSelectedClient(null); },
    onError: (err) => alert(err.message),
  });

  const [updateClient] = useMutation(UPDATE_CLIENT, {
    onCompleted: () => { refetchClients(); setClientModalOpen(false); setSelectedClient(null); },
    onError: (err) => alert(err.message),
  });

  const [deleteClient] = useMutation(DELETE_CLIENT, { onCompleted: () => refetchClients() });

  const [disconnectWhatsApp, { loading: disconnecting }] = useMutation(DISCONNECT_WHATSAPP, {
    onCompleted: () => refetchAccount(),
    onError: (err) => alert(err.message),
  });

  const [createTemplate, { loading: creatingTemplate }] = useMutation(CREATE_WHATSAPP_TEMPLATE, {
    onCompleted: () => { refetchTemplates(); setShowTemplateForm(false); setTemplateForm(emptyTemplate); },
    onError: (err) => alert(err.message),
  });

  const [updateTemplate, { loading: updatingTemplate }] = useMutation(UPDATE_WHATSAPP_TEMPLATE, {
    onCompleted: () => { refetchTemplates(); setShowTemplateForm(false); setEditingTemplate(null); setTemplateForm(emptyTemplate); },
    onError: (err) => alert(err.message),
  });

  const [deleteTemplate] = useMutation(DELETE_WHATSAPP_TEMPLATE, {
    onCompleted: () => refetchTemplates(),
    onError: (err) => alert(err.message),
  });

  // ── Derived data ──────────────────────────────────────────────────────────────

  const clients: Client[] = clientsData?.clients ?? [];
  const account: WhatsAppAccount | null = accountData?.whatsAppAccount ?? null;
  const messages: WhatsAppMessage[] = messagesData?.whatsAppMessages ?? [];
  const messagesCount: number = messagesData?.whatsAppMessagesCount ?? 0;
  const templates: WhatsAppTemplate[] = templatesData?.whatsAppTemplates ?? account?.templates ?? [];

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const toggleCheck = (id: number) =>
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setCheckedIds(prev => prev.size === clients.length ? new Set() : new Set(clients.map(c => c.id)));

  const handleClientSubmit = async (data: any) => {
    try {
      const input = {
        ...data,
        agencyId: Number(agencyId || data.agencyId || 1),
      };
      if (selectedClient) {
        await updateClient({ variables: { id: selectedClient.id, input } });
      } else {
        await createClient({ variables: { input } });
      }
    } catch (err) {
      console.error('Erreur client:', err);
    }
  };

  const handleDeleteClient = async (id: number) => {
    if (window.confirm('Supprimer ce client ?')) {
      await deleteClient({ variables: { id } });
    }
  };

  const startEditTemplate = (t: WhatsAppTemplate) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, content: t.content, language: t.language, variables: t.variables, isDefault: t.isDefault });
    setShowTemplateForm(true);
  };

  const handleTemplateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateTemplate({ variables: { id: editingTemplate.id, input: templateForm } });
    } else {
      createTemplate({ variables: { input: templateForm } });
    }
  };

  if (!user) return null;
  if (!agencyName) {
    return (
      <AdminLayout>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-4 rounded-lg m-6">
          Aucune agence associée à votre compte.
        </div>
      </AdminLayout>
    );
  }

  // ── Tabs config ───────────────────────────────────────────────────────────────

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'clients',   label: `Clients${clients.length ? ` (${clients.length})` : ''}`, icon: <Users className="w-4 h-4" /> },
    { key: 'overview',  label: 'WhatsApp', icon: <Smartphone className="w-4 h-4" /> },
    { key: 'history',   label: `Historique${account ? ` (${account.messagesCount})` : ''}`, icon: <Clock className="w-4 h-4" /> },
    { key: 'templates', label: 'Templates', icon: <BookOpen className="w-4 h-4" /> },
  ];

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clients & Communication</h1>
            <p className="text-sm text-gray-500 mt-0.5">Gérez vos clients et envoyez vos offres par email ou WhatsApp</p>
          </div>
          <div className="flex items-center gap-3">
            {account?.isActive && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                WhatsApp connecté
              </span>
            )}
            <button
              onClick={() => { setSelectedClient(null); setClientModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Nouveau client
            </button>
          </div>
        </div>

        {/* ── Tabs ───────────────────────────────────────────────────────────── */}
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon} {tab.label}
              {tab.key === 'overview' && account?.isActive && (
                <span className="w-2 h-2 bg-green-500 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* ══ Onglet Clients ═══════════════════════════════════════════════════ */}
        {activeTab === 'clients' && (
          <>
            {clientsLoading ? (
              <div className="flex justify-center items-center h-48">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <>
                {/* Barre d'actions groupées */}
                {checkedIds.size > 0 && (
                  <div className="flex items-center justify-between bg-blue-600 text-white rounded-xl px-5 py-3 mb-3">
                    <span className="text-sm font-medium">
                      {checkedIds.size} client{checkedIds.size > 1 ? 's' : ''} sélectionné{checkedIds.size > 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setOfferTargets(clients.filter(c => checkedIds.has(c.id)))}
                        className="flex items-center gap-2 px-4 py-1.5 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50"
                      >
                        <Send className="w-3.5 h-3.5" /> Envoyer une offre
                      </button>
                      <button
                        onClick={() => setCheckedIds(new Set())}
                        className="px-3 py-1.5 bg-blue-500 hover:bg-blue-400 rounded-lg text-sm"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={clients.length > 0 && checkedIds.size === clients.length}
                            onChange={toggleAll}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </th>
                        {['Nom', 'Email', 'Téléphone', 'Adresse', 'Actions'].map((h, i) => (
                          <th key={h} className={`px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 4 ? 'text-right' : 'text-left'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {clients.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-5 py-10 text-center text-gray-400 text-sm">
                            Aucun client — <button onClick={() => setClientModalOpen(true)} className="text-blue-600 hover:underline">ajouter le premier</button>
                          </td>
                        </tr>
                      ) : (
                        clients.map((client) => (
                          <tr key={client.id} className={`hover:bg-gray-50 ${checkedIds.has(client.id) ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={checkedIds.has(client.id)}
                                onChange={() => toggleCheck(client.id)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 text-sm font-semibold flex-shrink-0">
                                  {client.firstName[0]?.toUpperCase()}{client.lastName[0]?.toUpperCase()}
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                  {client.firstName} {client.lastName}
                                </span>
                              </div>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">{client.email || '—'}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">{client.phone || '—'}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-500">{client.address || '—'}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setOfferTargets([client])}
                                  title="Envoyer une offre"
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                >
                                  <Send className="w-3.5 h-3.5" /> Offre
                                </button>
                                <button
                                  onClick={() => { setSelectedClient(client); setClientModalOpen(true); }}
                                  title="Modifier"
                                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClient(client.id)}
                                  title="Supprimer"
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* ══ Onglet Vue d'ensemble WhatsApp ══════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {accountLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : account?.isActive ? (
              <>
                {/* Compte connecté */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                        <Smartphone className="w-7 h-7 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{account.displayName || 'WhatsApp Business'}</h3>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Connecté
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm mt-0.5">{account.phoneNumber}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { if (confirm('Déconnecter WhatsApp ? Vous devrez rescanner le QR.')) disconnectWhatsApp(); }}
                      disabled={disconnecting}
                      className="flex items-center gap-1 px-3 py-1.5 border border-red-200 rounded-lg text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Déconnecter
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Messages envoyés', value: account.messagesCount, icon: <Send className="w-5 h-5 text-blue-500" />, bg: 'bg-blue-50' },
                    { label: 'Templates', value: templates.length, icon: <BookOpen className="w-5 h-5 text-purple-500" />, bg: 'bg-purple-50' },
                    { label: 'Clients', value: clients.length, icon: <Users className="w-5 h-5 text-green-500" />, bg: 'bg-green-50' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>{stat.icon}</div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-green-500" /> Connecter WhatsApp
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Scannez le QR code avec votre téléphone — comme WhatsApp Web. Aucun compte Meta requis.
                  </p>
                </div>
                <div className="p-6">
                  <WhatsAppQRConnect
                    onConnected={() => refetchAccount()}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ Onglet Historique ════════════════════════════════════════════════ */}
        {activeTab === 'history' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{messagesCount} message{messagesCount !== 1 ? 's' : ''} envoyés</h3>
              <button onClick={() => refetchMessages()} className="text-gray-400 hover:text-gray-600">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {messagesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                Aucun message envoyé
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {messages.map(msg => {
                  const statusInfo = STATUS_STYLES[msg.status] || STATUS_STYLES.PENDING;
                  return (
                    <div key={msg.id} className="flex items-start gap-4 p-4 hover:bg-gray-50">
                      <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-gray-900">{msg.recipientName || msg.recipientPhone}</span>
                          {msg.recipientName && <span className="text-xs text-gray-400">{msg.recipientPhone}</span>}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        {msg.package && <p className="text-xs text-gray-500 mt-0.5">Offre : {msg.package.title}</p>}
                        {msg.errorMessage && <p className="text-xs text-red-500 mt-0.5">{msg.errorMessage}</p>}
                      </div>
                      <div className="text-right text-xs text-gray-400 flex-shrink-0">
                        <p>{new Date(msg.createdAt).toLocaleDateString('fr-FR')}</p>
                        <p>{new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                        {msg.readAt && <p className="text-green-500 mt-0.5">Lu</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ Onglet Templates ═════════════════════════════════════════════════ */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => { setEditingTemplate(null); setTemplateForm(emptyTemplate); setShowTemplateForm(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
              >
                <Plus className="w-4 h-4" /> Nouveau template
              </button>
            </div>

            {showTemplateForm && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
                </h3>
                <form onSubmit={handleTemplateSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom <span className="text-red-500">*</span></label>
                      <input
                        value={templateForm.name}
                        onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="ex: Offre standard"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Langue</label>
                      <select
                        value={templateForm.language}
                        onChange={e => setTemplateForm(p => ({ ...p, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="fr">Français</option>
                        <option value="ar">Arabe</option>
                        <option value="en">Anglais</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contenu <span className="text-red-500">*</span>
                      <span className="text-gray-400 font-normal ml-1">(utilisez {'{{variable}}'} pour les variables)</span>
                    </label>
                    <textarea
                      value={templateForm.content}
                      onChange={e => setTemplateForm(p => ({ ...p, content: e.target.value }))}
                      placeholder="✈️ Bonjour {{nom}}, découvrez notre offre pour {{destination}}..."
                      rows={5}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="tplDefault"
                      checked={templateForm.isDefault}
                      onChange={e => setTemplateForm(p => ({ ...p, isDefault: e.target.checked }))}
                      className="h-4 w-4 text-green-600 border-gray-300 rounded"
                    />
                    <label htmlFor="tplDefault" className="text-sm text-gray-700">Template par défaut</label>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => { setShowTemplateForm(false); setEditingTemplate(null); }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                      Annuler
                    </button>
                    <button type="submit" disabled={creatingTemplate || updatingTemplate}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                      {editingTemplate ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {templates.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-400 text-sm">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                Aucun template. Créez-en un pour personnaliser vos messages WhatsApp.
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(t => (
                  <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-4">
                    <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">{t.name}</span>
                        <span className="text-xs text-gray-400">{t.language}</span>
                        {t.isDefault && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Défaut</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2">{t.content}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEditTemplate(t)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => { if (confirm('Supprimer ce template ?')) deleteTemplate({ variables: { id: t.id } }); }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      <CustomerFormModal
        isOpen={clientModalOpen}
        onClose={() => { setClientModalOpen(false); setSelectedClient(null); }}
        onSubmit={handleClientSubmit}
        client={selectedClient}
        title={selectedClient ? 'Modifier le client' : 'Nouveau client'}
      />

      <SendOfferModal
        isOpen={offerTargets.length > 0}
        onClose={() => { setOfferTargets([]); setCheckedIds(new Set()); }}
        clients={offerTargets}
        agencyName={agencyName}
        waAccount={account}
        templates={templates}
        onSent={() => { setOfferTargets([]); setCheckedIds(new Set()); if (activeTab === 'history') refetchMessages(); refetchAccount(); }}
      />
    </AdminLayout>
  );
}
